import { useState } from "react";
import type { AnalyteGroupRow, AnalyteStudyRef, AnalyteUpdateResult } from "@probetalab/contracts";
import { run } from "../lib/api.js";
import { useToast } from "../lib/toast.js";
import { errorMessage } from "../lib/useQuery.js";
import { Alert, Button } from "./ui.js";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * «Eliminar del catálogo» for one analyte (pilot request of 2026-11-03, "borrar alguno"). Nothing
 * is deleted: the clave leaves every study (each a new version), disappears from search unless
 * «Mostrar eliminados» is on, and can be brought back. Results already captured and the reports
 * already issued do not change. The panel names the studies first; a study whose only analyte this
 * is blocks the removal, the same refusal the server gives.
 */
export function DeleteAnalytePanel({ code, name, rows, onDone, onBack }: { code: string; name: string; rows: AnalyteGroupRow[]; onDone: (r: AnalyteUpdateResult) => void; onBack: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const last = rows.filter((r) => !r.siblings.some((s) => s.kind !== "subtitle"));
  const open = rows.reduce((n, r) => n + r.open_items, 0);

  async function remove() {
    setBusy(true);
    try {
      const res = await run<"catalogue.analyte.set_active", AnalyteUpdateResult>("catalogue.analyte.set_active", { code, is_active: false, study_ids: [] });
      toast.success(`${code} eliminado del catálogo (${plural(res.studies.length, "estudio", "estudios")}). Los resultados anteriores se conservan.`);
      onDone(res);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div role="alertdialog" aria-label="Eliminar del catálogo" className="stack" style={{ gap: 10 }}>
      <Alert tone="amber">
        <strong>
          {code} · {name} se quitará de {plural(rows.length, "estudio", "estudios")}: {rows.map((r) => `${r.study.clave} · ${r.study.name}`).join(", ")}.
        </strong>{" "}
        Cada uno queda en una versión nueva y ya no se pide en las órdenes nuevas
        {open > 0 ? `; ${plural(open, "orden que aún no tiene resultados dejará", "órdenes que aún no tienen resultados dejarán")} de pedirlo` : ""}. No se borra nada: los resultados y los informes ya emitidos se conservan, y lo puedes volver a activar con «Mostrar eliminados».
      </Alert>
      {last.length > 0 && (
        <Alert tone="red">
          {last.map((r) => r.study.clave).join(", ")} {last.length === 1 ? "se quedaría" : "se quedarían"} sin analitos: {code} es {last.length === 1 ? "el único que tiene" : "el único que tienen"}. Agrega otro analito {last.length === 1 ? "al estudio" : "a esos estudios"} primero, o quita {last.length === 1 ? "el estudio" : "los estudios"} del catálogo.
        </Alert>
      )}
      <div className="row end">
        <Button variant="ghost" onClick={onBack}>
          Volver
        </Button>
        <Button variant="danger" icon="trash" busy={busy} disabled={last.length > 0} onClick={() => void remove()}>
          Eliminar del catálogo
        </Button>
      </div>
    </div>
  );
}

/**
 * A clave removed from the catalogue (listed with «Mostrar eliminados»): the studies it was removed
 * from, all ticked, and «Volver a activar». It returns at the end of each study's list.
 */
export function ReactivateAnalytePanel({ code, name, studies, onDone, onCancel }: { code: string; name: string; studies: AnalyteStudyRef[]; onDone: (r: AnalyteUpdateResult) => void; onCancel: () => void }) {
  const toast = useToast();
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(studies.map((s) => s.id)));
  const [busy, setBusy] = useState(false);
  const toggle = (id: string) =>
    setChosen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  async function reactivate() {
    setBusy(true);
    try {
      const res = await run<"catalogue.analyte.set_active", AnalyteUpdateResult>("catalogue.analyte.set_active", { code, is_active: true, study_ids: studies.filter((s) => chosen.has(s.id)).map((s) => s.id) });
      toast.success(`${code} activo otra vez en ${plural(res.studies.length, "estudio", "estudios")}.`);
      onDone(res);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 12 }}>
      <Alert tone="blue">
        {code} · {name} está eliminado del catálogo. Al volver a activarlo regresa al final de la lista de cada estudio que elijas, en una versión nueva; ahí lo puedes mover.
      </Alert>
      <section className="stack" style={{ gap: 6 }} aria-label="Estudios donde vuelve">
        <strong>Estudios de donde se quitó ({studies.length})</strong>
        {studies.map((s) => (
          <label key={s.id} className="list-row" style={{ minHeight: 48, gap: 10 }}>
            <input type="checkbox" aria-label={`Volver a activar en ${s.clave}`} checked={chosen.has(s.id)} onChange={() => toggle(s.id)} style={{ width: 22, height: 22 }} />
            <span className="mono muted" style={{ width: 90 }}>
              {s.clave}
            </span>
            <span className="grow">{s.name}</span>
          </label>
        ))}
      </section>
      <div className="row end">
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="primary" icon="check" busy={busy} disabled={chosen.size === 0} onClick={() => void reactivate()}>
          Volver a activar
        </Button>
      </div>
    </div>
  );
}
